"use client";

import * as React from "react";
import { cn } from "@marketing-sites/utils/cn";
import { getAnalyticsRegion } from "@marketing-sites/analytics/get-analytics-region";
import { triggerAnalyticsClick } from "@marketing-sites/analytics/trigger-analytics-event";
import { TrackingType } from "@marketing-sites/analytics/types";
import { useCarousel } from "./carousel.context";
import { useAutoplayProgress } from "./carousel.hooks";
import type {
  AutoplayProgressProps,
  ThumbnailProgressRenderProps,
  ThumbnailProps,
} from "./carousel.types";

const DEFAULT_ANIMATION_NAME = "carouselAutoplay";
const DEFAULT_KEYFRAMES = `@keyframes ${DEFAULT_ANIMATION_NAME} { from { transform: scaleX(0); } to { transform: scaleX(1); } }`;

/**
 * Render-prop component for the autoplay progress animation.
 * A default `scaleX(0 → 1)` keyframe (`"carouselAutoplay"`) is injected automatically.
 */
export function AutoplayProgress({
  animationName = DEFAULT_ANIMATION_NAME,
  children,
}: AutoplayProgressProps) {
  const { emblaApi } = useCarousel();
  const progressRef = React.useRef<HTMLElement | null>(null);
  const refCallback: React.RefCallback<HTMLElement> = React.useCallback(
    (node) => {
      progressRef.current = node;
    },
    [],
  );
  const { showAutoplayProgress } = useAutoplayProgress(emblaApi, progressRef);
  return (
    <>
      {animationName === DEFAULT_ANIMATION_NAME && (
        <style>{DEFAULT_KEYFRAMES}</style>
      )}
      {children({
        ref: refCallback,
        isPlaying: showAutoplayProgress,
        animationName,
      })}
    </>
  );
}

/** Flex wrapper for thumbnail buttons. Defaults to a horizontal row. */
export function Thumbnails({
  className,
  ...rest
}: React.ComponentProps<"div">) {
  return <div className={cn("flex gap-2", className)} {...rest} />;
}

const defaultProgress = ({
  ref,
  isPlaying,
  animationName,
  isSelected,
}: ThumbnailProgressRenderProps) => (
  <div
    ref={ref}
    className={cn(
      "absolute bottom-0 left-0 h-0.5 w-full origin-left bg-current",
      isPlaying && isSelected ? "opacity-100" : "opacity-0",
    )}
    style={{ animation: `${animationName} linear` }}
  />
);

function endsSelectionWithin(el: HTMLElement): boolean {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
    return false;
  }
  return selection.getRangeAt(0).intersectsNode(el);
}

/**
 * A thumbnail button that scrolls to its slide on click.
 * Sets `data-selected` when active; use for CSS styling: `data-selected:opacity-100`.
 */
export function Thumbnail({
  index,
  analyticsId,
  className,
  progress = defaultProgress,
  children,
  "aria-label": ariaLabel,
  ...rest
}: ThumbnailProps) {
  const { emblaApi, setHoveredIndex } = useCarousel();
  const [isSelected, setIsSelected] = React.useState(false);

  React.useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () =>
      setIsSelected(emblaApi.selectedScrollSnap() === index);
    onSelect();
    emblaApi.on("select", onSelect).on("reInit", onSelect);
  }, [emblaApi, index]);

  const handleMouseEnter = React.useCallback(
    () => setHoveredIndex(index),
    [setHoveredIndex, index],
  );
  const handleMouseLeave = React.useCallback(
    () => setHoveredIndex(null),
    [setHoveredIndex],
  );

  const activate = React.useCallback(
    (target: HTMLElement) => {
      if (!emblaApi) return;

      if (!isSelected) {
        const location = getAnalyticsRegion(target) ?? "Unknown";
        const raw =
          ariaLabel ??
          target.innerText?.replace(/\s+/g, " ").trim() ??
          `Slide ${index + 1}`;
        const text = raw.length > 25 ? `${raw.slice(0, 25).trim()}…` : raw;
        triggerAnalyticsClick({
          text: `Carousel: ${text}`,
          location,
          type: TrackingType.Button,
          analyticsId,
        });

        emblaApi.scrollTo(index);
        emblaApi.plugins()?.autoplay?.reset();
      }
    },
    [emblaApi, index, ariaLabel, isSelected, analyticsId],
  );

  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (endsSelectionWithin(event.currentTarget)) return;
      activate(event.currentTarget);
    },
    [activate],
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.target !== event.currentTarget) return;
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      if (event.repeat) return;
      activate(event.currentTarget);
    },
    [activate],
  );

  // Not a <button>: browsers block text selection inside one (WEB-2743).
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-selected={isSelected || undefined}
      aria-current={isSelected || undefined}
      aria-label={ariaLabel}
      className={cn(
        "relative overflow-hidden focus-visible:outline-2 focus-visible:outline-offset-2",
        className,
      )}
      {...rest}
    >
      {children}
      <AutoplayProgress>
        {({ ref, isPlaying, animationName }) =>
          progress({ ref, isPlaying, animationName, isSelected })
        }
      </AutoplayProgress>
    </div>
  );
}
